// components/ContinueWatchingSection.js
import React from 'react';
import { motion } from 'framer-motion';
import { Play, RotateCcw } from 'lucide-react';

const ContinueWatchingSection = ({ movies, watchProgress, onPlayVideo }) => {
  if (movies.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="py-12 bg-gradient-to-b from-gray-900/80 to-gray-950/80 backdrop-blur-sm"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-4 mb-8">
          <motion.div
            className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg"
            whileHover={{ rotate: 5, scale: 1.05 }}
          >
            <RotateCcw className="w-6 h-6 text-white" />
          </motion.div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Continuar Viendo</h2>
            <p className="text-gray-400 text-sm mt-2">
              {movies.length} {movies.length === 1 ? 'película' : 'películas'} en progreso
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {movies.map((movie) => {
            const progress = watchProgress[movie.id] || 0;
            return (
              <ContinueWatchingCard
                key={movie.id}
                movie={movie}
                progress={progress}
                onPlay={onPlayVideo}
              />
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

const ContinueWatchingCard = ({ movie, progress, onPlay }) => {
  return (
    <motion.div
      className="group relative bg-gray-800/30 rounded-xl overflow-hidden cursor-pointer backdrop-blur-sm border border-gray-700/50 hover:border-green-500/50 transition-all duration-300"
      whileHover={{ scale: 1.05, y: -5 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onPlay(movie, progress)}
    >
      <div className="relative aspect-video">
        <img
          src={movie.image}
          alt={movie.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Progress Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
          <div className="flex items-center gap-2 mb-1">
            <RotateCcw className="w-3 h-3 text-green-400" />
            <span className="text-green-400 text-xs font-semibold">{progress}%</span>
          </div>
          <div className="w-full bg-gray-700/50 rounded-full h-1.5">
            <div 
              className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Play Button */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40">
          <motion.div
            className="bg-green-500/80 backdrop-blur-sm rounded-full p-3"
            whileHover={{ scale: 1.1 }}
          >
            <Play className="w-5 h-5 text-white fill-white" />
          </motion.div>
        </div>
      </div>
      
      <div className="p-3">
        <h3 className="text-white font-semibold text-sm line-clamp-1 group-hover:text-green-400 transition-colors">
          {movie.title}
        </h3>
        <p className="text-gray-400 text-xs mt-1">Continuar viendo</p>
      </div>
    </motion.div>
  );
};

export default ContinueWatchingSection;